import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { debounce } from 'lodash';
import { checkUTRReference } from 'store/collectionSlice';
import { Box, Text, Flex, Button, Image } from 'atoms';
import { Rupee, Highlighted } from 'components/common';
import upiNotFound from 'assets/upi-not-found.svg';
import * as Styled from './UPIAutoComplete.style';

// const format = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/; // eslint-disable-line
const onlyNumbers = /^[0-9\b]+$/;

const UPI_REF_LENGTH = 8;

export function UPIAutoCompleteManual({
	showUpiPopup,
	storeData,
	setDynamicUPIData,
	upiAmount,
	cashierVerified,
	manualModeEditData,
}) {
	const dispatch = useDispatch();
	const [refernceNo, setReferenceNo] = useState([]);
	const [UPISearchVal, setUPISearchVal] = useState('');
	const [errorVal, setErrorVal] = useState('');
	const [loading, setloading] = useState(false);

	console.log(manualModeEditData);

	useEffect(() => {
		setUPISearchVal('');
		setErrorVal('');
		if (manualModeEditData?.data?.reference_number) {
			setUPISearchVal(manualModeEditData?.data?.reference_number);
			// setReferenceNo(manualModeEditData?.data?.reference_number);
		}
	}, [manualModeEditData?.data?.reference_number, showUpiPopup]);

	const getUpiData = useCallback(
		(queryParams) => {
			setloading(true);
			dispatch(checkUTRReference(queryParams))
				.then((res) => {
					setReferenceNo(res.data);
				})
				.finally(() => {
					setloading(false);
				});
		},
		[dispatch]
	);

	const debouncedSearch = useRef(
		debounce(async (query) => {
			const queryParams = {
				store_id: storeData?.store_id,
				salesman_id: storeData?.salesman_id,
				amount: upiAmount,
				query,
			};

			getUpiData(queryParams);
		}, 500)
	).current;

	useEffect(
		() => () => {
			debouncedSearch.cancel();
		},
		[debouncedSearch]
	);

	// disabled the upi auto search, added button click to trigger the api call
	// useEffect(() => {
	// 	if (UPISearchVal?.length >= UPI_REF_LENGTH) {
	// 		debouncedSearch(UPISearchVal);
	// 	}
	// }, [debouncedSearch, UPISearchVal]);

	const handleChange = (e) => {
		const { value } = e.target;

		if (value.length >= UPI_REF_LENGTH) {
			setErrorVal('');
		}
		// else {
		// 	setErrorVal(`Enter atleast ${UPI_REF_LENGTH} reference number`);
		// }

		if (!onlyNumbers.test(value)) {
			setErrorVal('Number only allowed');
		} else {
			setUPISearchVal(value);
		}
	};

	const handleSelectUPI = (data, bank_statement_id = null) => {
		setDynamicUPIData({
			reference_number: data,
			bank_statement_id,
			upiAmount,
			type: 'Manual',
		});
	};

	function triggerUPISearch() {
		debouncedSearch(UPISearchVal);
	}

	if (!showUpiPopup) {
		return null;
	}

	const data_state = ['EDITDATA', 'EDITAMOUNTORDATA', '', 'DEFAULT'];
	const getDataStateCheck = (val) => {
		if (data_state.includes(val?.state) || val?.state === undefined) {
			return false;
		}
		if (val?.state === 'PERSIST') {
			return true;
		}

		return true;
	};

	return (
		<Box mt="2rem">
			<Box marginBottom="1rem">
				<Styled.StoreName>{storeData?.store_name || 'NA'}</Styled.StoreName>
				<Styled.InvoiceNumber>{storeData?.invoice_number || ''}</Styled.InvoiceNumber>
			</Box>
			<Box marginBottom="1.25rem">
				<Styled.InputLabel>*UTR Ref no:</Styled.InputLabel>
				<Styled.InputField
					value={UPISearchVal}
					placeholder="Enter reference number(minimum 12 characters)"
					onChange={handleChange}
					maxLength={20}
					disabled={getDataStateCheck(manualModeEditData?.data)}
				/>
				{UPISearchVal !== '' && <Styled.ClearIcon onClick={() => setUPISearchVal('')} />}
				<Styled.InputErrorMsg as="p">{errorVal}</Styled.InputErrorMsg>
			</Box>
			<Flex justifyContent="space-between" alignItems="center" mb="1rem">
				<Styled.GoBtn
					type="primary"
					disabled={UPISearchVal.length < UPI_REF_LENGTH}
					onClick={triggerUPISearch}
				>
					Search UPI
				</Styled.GoBtn>
				<Flex justifyContent="space-between">
					<Button
						bg="transparent"
						p="0"
						border="0"
						cursor="pointer"
						disabled={
							refernceNo?.length > 0 ||
							UPISearchVal.length < UPI_REF_LENGTH ||
							!onlyNumbers.test(UPISearchVal) ||
							cashierVerified
						}
						onClick={() => handleSelectUPI(UPISearchVal)}
					>
						<Flex alignItems="center">
							<FormOutlined
								className="hasDisabled"
								style={{ color: '#007FFF', marginRight: '8px' }}
							/>
							<Text fontSize="10px" lineHeight="15px" color="#007FFF">
								Add manually
							</Text>
						</Flex>
					</Button>
				</Flex>
			</Flex>

			{refernceNo?.length > 0 && !loading && (
				<Box as="ul" height="200px" overflow="auto">
					{refernceNo?.map((data) => (
						<Styled.SearchList
							key={data?.bank_statement_id}
							as="li"
							onClick={() =>
								handleSelectUPI(data?.transaction_number, data?.bank_statement_id)
							}
							cursor="pointer"
							alignItems="center"
						>
							<Box fontSize="14px" lineHeight="15px" color="#000000">
								<Highlighted
									text={data?.transaction_number || 'NA'}
									highlight={UPISearchVal}
								/>
								<Text fontSize="8px">{data?.remitter_name || ''}</Text>
							</Box>
							<Rupee style={{ fontWeight: '300', fontSize: '10px' }}>
								{data?.credit || 0}
							</Rupee>
						</Styled.SearchList>
					))}
				</Box>
			)}

			{refernceNo?.length === 0 && !loading && (
				<Flex
					flexDirection="column"
					justifyContent="center"
					alignItems="center"
					minHeight="100px"
				>
					<Image src={upiNotFound} alt="upiNotFound" />
					<Text textAlign="center" fontSize="10px" color="#484848" mt=".5rem">
						Search Not Found
					</Text>
				</Flex>
			)}
			{loading && (
				<Text width="100%" textAlign="center" pt="4rem" minHeight="100px">
					Loading...
				</Text>
			)}
		</Box>
	);
}

UPIAutoCompleteManual.propTypes = {
	showUpiPopup: PropTypes.bool,
	storeData: PropTypes.object,
	setDynamicUPIData: PropTypes.func,
	upiAmount: PropTypes.any,
	cashierVerified: PropTypes.bool,
	manualModeEditData: PropTypes.object,
};
